import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { WarningDialogComponent } from '../../shared/components/warning-dialog/warning-dialog.component';
import { Observable } from 'rxjs';
import { WarningDialogData } from '../../shared/model/query-interface';
import { ComponentType } from '@angular/cdk/portal';
import { MenuDialogComponent } from '../../shared/components/menu-dialog/menu-dialog.component';
import {
  MenuDialogData,
  RecipeShareParameter,
  Recipe,
  RecipeSharePayload,
  MenuDialogAction,
  ShareRecipeResponse,
  RecipeVariantDetails,
  AddAttachmentsDialogData,
} from '../../shared/model';
import { RecipeService } from '../recipe-services/recipe.service';
import { SnackbarService } from '../snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmLeaveDialogComponent } from '../../shared/components/confirm-leave-dialog/confirm-leave-dialog/confirm-leave-dialog.component';
import { AddAttachmentsDialogComponent } from '../../shared/components/add-attachments-dialog/add-attachments-dialog.component';

@Injectable({ providedIn: 'root' })
export class DialogService {
  private defaultWarningConfig: WarningDialogData;
  constructor(
    private _dialog: MatDialog,
    private _recipeService: RecipeService,
    private _snackbarService: SnackbarService,
    private _translateService: TranslateService,
  ) {
    this.defaultWarningConfig = {
      iconType: 'warning',
      message: this._translateService.instant('unsavedChanges'),
      confirmButtonText: this._translateService.instant('yes'),
      cancelButtonText: this._translateService.instant('no'),
      singleButton: false,
    };
  }

  /**
   * Opens a dialog with the specified component and configuration.
   *
   * @template T
   * @param {*} component
   * @param {MatDialogConfig<T>} [config]
   * @return {*}
   * @memberof DialogService
   */
  openDialog<T = unknown>(
    component: ComponentType<unknown>,
    config?: MatDialogConfig<T>,
  ) {
    const defaultConfig: MatDialogConfig<T> = {
      disableClose: true,
      minWidth: '37.5rem',
      ...config,
    };
    return this._dialog.open(component, defaultConfig);
  }

  /**
   * Opens a common dialog with the provided data for type "warning", "error", "info".
   *
   * @param {WarningDialogData} data
   * @return {*}  {Observable<boolean>}
   * @memberof DialogService
   */
  commonDialog(
    data: WarningDialogData = this.defaultWarningConfig,
  ): Observable<boolean> {
    const dialogRef = this._dialog.open(WarningDialogComponent, {
      width: '39.5rem',
      data: data,
      disableClose: true,
    });
    return dialogRef.afterClosed() as Observable<boolean>;
  }
  /**
   * Opens a common menu dialog with the provided data.
   *
   * @param {*} data
   * @return {*}
   * @memberof DialogService
   */
  public commonMenuDialog(data: MenuDialogData) {
    return this._dialog.open(MenuDialogComponent, {
      width: '55.5rem',
      data: data,
    });
  }
  /**
   * Closes all open dialog windows mainly used for 'X' button.
   *
   * @memberof DialogService
   */
  public closeAllDialogs() {
    this._dialog.closeAll();
  }
  /**
   * Opens a dialog to share a recipe.
   *
   * @param {Recipe | RecipeShareParameter} [recipe]
   * @memberof DialogService
   */
  public shareRecipeDialog(
    recipe?: Recipe | RecipeShareParameter,
    isToggleDisabled?: boolean,
  ) {
    this.commonMenuDialog({
      title: 'shareRecipeDialogTitle',
      subtitle: 'manageAccess',
      input: {
        label: 'addEmailOrNames',
        value: '',
      },
      toggles: [
        {
          label: 'readOnly',
          default: true,
          subText: 'readOnlySubText',
          disabled: isToggleDisabled,
        },
      ],
      buttons: [
        {
          text: 'shareRecipeButton',
          color: 'primary',
          icon: 'send',
          action: (data?: MenuDialogAction) => {
            const recipeSharePayload: RecipeSharePayload = {
              recipeId: recipe?.recipeId || 0,
              recipeAccessors: {
                accessorEmails: data?.input?.value
                  ? data.input.value.split(',').map((email) => email.trim())
                  : [],
                isReadOnly: data?.toggles?.['readOnly'] || false,
              },
            };
            this._recipeService
              .shareRecipe(recipeSharePayload)
              .then((response) => {
                if (response && (response as ShareRecipeResponse).isSuccess) {
                  this._snackbarService.open({
                    message:
                      this._translateService.instant('recipeShareSuccess'),
                    type: 'success',
                  });
                } else {
                  this._snackbarService.open({
                    message: this._translateService.instant('recipeShareError'),
                    type: 'error',
                  });
                }
              });
            return { shouldClose: true };
          },
        },
        {
          text: 'copyLink',
          color: 'not-primary',
          icon: 'link',
          action: () => {
            const id =
              (recipe as RecipeShareParameter)?.recipeIdentifier ||
              (recipe as Recipe)?.recipeIdentifier;
            const shareLink = `${window.location.origin}/recipe/recipe-details/${id}`;
            navigator.clipboard.writeText(shareLink).then(() => {
              this._snackbarService.open({
                message: this._translateService.instant(
                  'copiedLinkSuccessfully',
                ),
                type: 'success',
              });
            });
            return { shouldClose: false };
          },
        },
      ],
    });
  }
  /**
   * Opens a dialog to download a recipe.
   *
   * @param {Recipe | RecipeShareParameter} [recipe]
   * @memberof DialogService
   */
  public downloadRecipe(
    recipe?: Recipe | RecipeShareParameter,
    currencyName?: string,
    currencySymbol?: string,
    isBakersEnabled?: boolean,
  ) {
    let list: RecipeVariantDetails[] = [];
    let subList: string[] = [];
    const recipeCategory = recipe?.categoryName ?? '';

    if ('recipeVariantDetails' in (recipe ?? {})) {
      const details =
        (recipe as { recipeVariantDetails?: RecipeVariantDetails[] })
          .recipeVariantDetails ?? [];
      list = details;
      subList = details
        .map((v: RecipeVariantDetails) => v.variantType)
        .filter((type): type is string => type !== undefined);
    }

    const variantMap = new Map<string, RecipeVariantDetails>();
    list.forEach((variant: RecipeVariantDetails) => {
      if (variant.variantTypeName !== undefined) {
        variantMap.set(variant.variantTypeName, variant);
      }
    });
    this.commonMenuDialog({
      title: 'downloadPresentation',
      subtitle: 'selectFormulasToInclude',
      list,
      subList,
      isBakersEnabled,
      recipeCategory,
      buttons: [
        {
          text: 'downloadPresentation',
          color: 'primary',
          icon: 'download',
          action: (data?: MenuDialogAction) => {
            const _selectedItems = list.filter((variant) =>
              data?.selectedItems?.includes(variant.recipeVariantId),
            );
            const payload = {
              recipeId: recipe?.recipeId ?? 0,
              variantsId: _selectedItems
                .map((item) => item?.recipeVariantId)
                .filter((id): id is number => typeof id === 'number'),
              currencyCode: currencyName ?? '',
              currencySymbol: currencySymbol ?? '',
              includeBakersPercentage: data?.includeBakers ?? false,
            };
            this._recipeService
              .downloadVariantPPTX(payload)
              .then((response) => {
                const contentDisposition = response.headers.get(
                  'Content-Disposition',
                );
                const getFileName = (cd: string | null): string => {
                  if (!cd) return 'downloaded_file.pptx';
                  const matches = cd.match(/filename="([^;]+)"/);
                  return matches ? matches[1] : 'downloaded_file.pptx';
                };
                const url = window.URL.createObjectURL(response.body as Blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = getFileName(contentDisposition); // Set your desired file name and extension
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url); // Clean up the object URL
                a.remove(); // Remove the hidden link
              });
            return { shouldClose: true };
          },
        },
      ],
    });
  }

  /**
   * Opens a dialog to confirm page leave warning.
   *
   * @param {*} [component=WarningDialogComponent]
   * @return {*}  {Observable<boolean>}
   * @memberof DialogService
   */
  public pageLeaveWarningDialog(): Observable<boolean | string> {
    const dialogRef = this._dialog.open(ConfirmLeaveDialogComponent, {
      minWidth: '700px',
      disableClose: true,
    });
    return dialogRef.afterClosed() as Observable<string | boolean>;
  }

  /**
   * Opens a dialog to attach documents.
   *
   * @param {AddAttachmentsDialogData} data
   * @return {*}
   * @memberof DialogService
   */
  public attachDocuments(data: AddAttachmentsDialogData) {
    return this._dialog.open(AddAttachmentsDialogComponent, {
      minWidth: '45.5rem',
      data: data,
    });
  }
}
